import { gql as contentfulGQL, gql } from '@apollo/client'
import { CONTENT_AVAIL_FRAG } from '../ContentAvailabilityService'

export const getAllProjectsQuery = gql`
  query getAllProjects {
    projects {
      id
      projectType
      name
      pifEnabled
      slug
      logoCloudinaryPath
      discoveryPosterTransformation
      discoveryPosterCloudinaryPath
      videos {
        id
        guid
        page
        title
      }
    }
  }
`

export const GET_OUR_SHOWS = gql`
  query getOurShows($exclude: [String]!) {
    listWatchables(
      first: 100
      input: {
        filter: { type: { in: [SERIES, MOVIE] }, slug: { nin: $exclude } }
        watchableOnly: true
        includePrerelease: false
        includePresale: false
      }
    ) {
      edges {
        node {
          __typename
          ... on Node {
            id
          }
          ... on ContentSeries {
            id
            image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
              cloudinaryPath
            }
            project {
              id
              slug
              name
              releaseDate
            }
          }
          ... on ContentMovie {
            id
            image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
              cloudinaryPath
            }
            project {
              id
              slug
              name
              releaseDate
            }
          }
        }
      }
    }
  }
`

export const GET_ALL_SHOWS = gql`
  query getAllShows($exclude: [String]!) {
    listWatchables(
      first: 100
      input: {
        filter: { type: { in: [SERIES] }, slug: { nin: $exclude } }
        includePrerelease: false
        includePresale: false
      }
    ) {
      edges {
        node {
          __typename
          ... on Node {
            id
          }
          ... on ContentSeries {
            id
            image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
              cloudinaryPath
            }
            project {
              id
              slug
              name
              releaseDate
              sortOrder
            }
            firstWatchable {
              __typename
              ... on ContentEpisode {
                id
                watchableAvailabilityStatus
              }
            }
          }
        }
      }
    }
  }
`

export const GET_ALL_MOVIES = gql`
  query getAllMovies($exclude: [String]!) {
    listWatchables(
      first: 100
      input: {
        filter: { type: { in: [MOVIE] }, slug: { nin: $exclude } }
        includePrerelease: false
        includePresale: false
      }
    ) {
      edges {
        node {
          __typename
          ... on ContentMovie {
            id
            image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
              cloudinaryPath
            }
            project {
              id
              slug
              name
              releaseDate
              sortOrder
            }
          }
          ... on ContentWatchableAvailability {
            watchableAvailabilityStatus
          }
        }
      }
    }
  }
`

const EpisodeGuildEarlyAccessFragment = gql`
  fragment EpisodeGuildEarlyAccess on Episode {
    isAngelGuildOnly
    prereleaseAvailableFor
    isTrailer
    guildAvailableDate
    publiclyAvailableDate
    earlyAccessDate
    unavailableReason
  }
`

export const projectMetaFragment = gql`
  fragment ProjectMeta on Project {
    id
    releaseDate
    discoveryPosterLandscapeCloudinaryPath
    discoveryVideoLandscapeUrl
    franchiseDetails {
      watchSequence
      franchiseId
      slug
    }
    logoCloudinaryPath
    livestreamsCount
    name
    pifEnabled
    pifExplainerText
    pifExplainerVideoUrl
    projectType
    public
    title {
      __typename
      ... on Node {
        id
      }
      ... on ContentDisplayable {
        id
        contentRatings {
          country
          rating
          summary
          source
        }
        genres
        landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
          aspect
          category
          cloudinaryPath
        }
        portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
          aspect
          category
          cloudinaryPath
        }
        logo: image(aspect: "original", category: LOGO) {
          aspect
          category
          cloudinaryPath
        }
        squarePortraitNonTitleArt: image(aspect: "1:1", category: NON_TITLE_ART) {
          aspect
          category
          cloudinaryPath
        }
        longDescription: description(version: LONG)
        mediumDescription: description(version: MEDIUM)
        shortDescription: description(version: SHORT)
      }
      ... on ContentWatchable {
        id
        duration
      }
    }
    slug
    streamingAtLabel
    highestScore
    videos(page: "trailer") {
      id
      posterLandscapeCloudinaryPath
      source {
        duration
        url(input: $streamingArgs)
      }
      subtitle
      title
    }
  }
`

export const getProjectMetaQuery = gql`
  ${projectMetaFragment}
  query getProjectMeta($slug: String!, $streamingArgs: StreamingUrlArgs) {
    project(slug: $slug) {
      ...ProjectMeta
    }
  }
`

/**
 * Extended project meta query for hub pages (/movies/ and /shows/).
 * Fetches still images, title art, cast/crew (including episode-level for series).
 * Used by getProjectAbout when includeStills option is true.
 */
export const getProjectMetaForHubPageQuery = gql`
  ${projectMetaFragment}
  query getProjectMetaForHubPage($slug: String!, $streamingArgs: StreamingUrlArgs) {
    project(slug: $slug) {
      ...ProjectMeta
      title {
        ... on ContentDisplayable {
          portraitTitleArt: image(aspect: "2:3", category: TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          landscapeTitleArt: image(aspect: "16:9", category: TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          still1: image(aspect: "16:9", category: STILL_1) {
            aspect
            category
            cloudinaryPath
          }
          still2: image(aspect: "16:9", category: STILL_2) {
            aspect
            category
            cloudinaryPath
          }
          still3: image(aspect: "16:9", category: STILL_3) {
            aspect
            category
            cloudinaryPath
          }
          still4: image(aspect: "16:9", category: STILL_4) {
            aspect
            category
            cloudinaryPath
          }
        }
        ... on ContentPeople {
          cast {
            name
            character
            images {
              category
              cloudinaryPath
            }
          }
          crew {
            name
            job
            department
          }
        }
        ... on ContentSeries {
          seasons(first: 10) {
            edges {
              node {
                id
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      cast {
                        name
                        character
                        images {
                          category
                          cloudinaryPath
                        }
                      }
                      crew {
                        name
                        job
                        department
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

/**
 * Extended project meta query that includes seasons/episodes data.
 * Used by getProjectAbout when includeSeasons option is true.
 * This combines the fields from projectMetaFragment with the seasons data
 * that was previously fetched separately by getWatchProjectEarlyAccess.
 */
export const getProjectMetaWithSeasonsQuery = gql`
  ${projectMetaFragment}
  ${CONTENT_AVAIL_FRAG}
  query getProjectMetaWithSeasons($slug: String!, $streamingArgs: StreamingUrlArgs) {
    project(slug: $slug) {
      ...ProjectMeta
      title {
        ... on ContentSeries {
          seasons(first: 10) {
            edges {
              node {
                id
                name
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      name
                      description
                      episodeNumber
                      posterCloudinaryPath
                      posterLandscapeCloudinaryPath
                      slug
                      subtitle
                      duration
                      isGuildShareAvailable
                      project {
                        id
                        slug
                      }
                      watchPosition {
                        id
                        position
                      }
                      ...ContentAvailability
                      cast {
                        name
                        character
                        images {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                      crew {
                        name
                        job
                        images {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                      ... on ContentDisplayable {
                        id
                        still: image(aspect: "16:9", category: STILL_1) {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentSharable {
          isGuildShareAvailable
        }
        ... on ContentWatchableAvailability {
          watchableAvailabilityStatus
          watchableReasons {
            __typename
            ... on LinkshareAvailabilityAction {
              startDatetime
            }
          }
        }
        ... on ContentDisplayable {
          landscapeNonTitleImage: image(aspect: "16:9", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          portraitNonTitleImage: image(aspect: "2:3", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          squareNonTitleImage: image(aspect: "1:1", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
        }
      }
      franchiseDetails {
        watchSequence
        franchiseId
        slug
      }
    }
  }
`

export const GET_MOVIE_AND_SPECIAL_WATCHABLES_QUERY = gql`
  ${CONTENT_AVAIL_FRAG}
  query getMovieAndSpecialsWatchables($slug: String!) {
    listWatchables(
      first: 100
      input: {
        filter: { projectSlug: { eq: $slug }, type: { in: [MOVIE, SPECIAL, SNEAK_PEEK, BONUS_CONTENT, TRAILER] } }
      }
    ) {
      edges {
        node {
          __typename
          ... on ContentMovie {
            id
            slug
            name
            subtitle
            description
            posterLandscapeCloudinaryPath
            url
            credits
            duration
            project {
              id
              slug
            }
            watchPosition {
              id
              position
              updatedAt
            }
            isDownloadable
          }
          ... on ContentSpecial {
            id
            slug
            name
            subtitle
            description
            posterLandscapeCloudinaryPath
            url
            credits
            duration
            project {
              id
              slug
            }
            watchPosition {
              id
              position
              updatedAt
            }
          }
          ... on ContentSneakPeek {
            id
            slug
            subtitle
            name
            nodeName
            posterLandscapeCloudinaryPath
            url
            credits
            duration
            description
            project {
              id
              slug
            }
            watchPosition {
              id
              position
              updatedAt
            }
          }
          ... on ContentBonus {
            id
            slug
            subtitle
            name
            nodeName
            posterLandscapeCloudinaryPath
            url
            credits
            duration
            description
            project {
              id
              slug
            }
            watchPosition {
              id
              position
              updatedAt
            }
          }
          ... on ContentTrailer {
            id
            slug
            subtitle
            name
            description
            posterLandscapeCloudinaryPath
            url
            credits
            duration
            project {
              id
              slug
            }
            watchPosition {
              id
              position
              updatedAt
            }
          }
          ...ContentAvailability
        }
      }
    }
  }
`

export const GET_ALL_SITEMAP_WATCHABLES = gql`
  ${CONTENT_AVAIL_FRAG}
  query getAllSitemapWatchables {
    listWatchables(
      first: 8000
      input: {
        filter: { type: { in: [MOVIE, SPECIAL, SNEAK_PEEK, BONUS_CONTENT, TRAILER, EPISODE] } }
        includePrerelease: false
        includePresale: false
      }
    ) {
      edges {
        node {
          __typename
          ... on ContentMovie {
            id
            slug
            project {
              id
              slug
            }
          }
          ... on ContentSpecial {
            id
            slug
            project {
              id
              slug
            }
          }
          ... on ContentSneakPeek {
            id
            slug
            project {
              id
              slug
            }
          }
          ... on ContentBonus {
            id
            slug
            project {
              id
              slug
            }
          }
          ... on ContentTrailer {
            id
            slug
            project {
              id
              slug
            }
          }
          ... on ContentEpisode {
            id
            slug
            subtitle
            episodeNumber
            project {
              id
              slug
            }
            season {
              id
              seasonNumber
            }
          }
          ...ContentAvailability
        }
      }
    }
  }
`

export const RESUME_WATCHING_FRAGMENT = gql`
  fragment ResumeWatchingPosition on WatchPosition {
    watchableGuid
    position
    updatedAt
    content {
      __typename
      ... on Node {
        id
      }
      ... on ContentWatchable {
        duration
      }
      ... on ContentEpisode {
        id
        episodeNumber
        season {
          id
          seasonNumber
        }
        project {
          id
          slug
        }
      }
      ... on ContentMovie {
        id
        project {
          id
          slug
        }
      }
      ... on ContentSpecial {
        id
        project {
          id
          slug
        }
      }
    }
  }
`

export const GET_RESUME_WATCHING_FOR_PROJECT = gql`
  ${RESUME_WATCHING_FRAGMENT}
  query getResumeWatchingForProject($projectSlug: String!) {
    resumeWatching(first: 1, projectSlug: $projectSlug) {
      edges {
        node {
          ...ResumeWatchingPosition
        }
      }
    }
  }
`

export const getProjectQuery = gql`
  ${CONTENT_AVAIL_FRAG}
  query getProject($slug: String!) {
    project(slug: $slug) {
      id
      releaseDate
      highestScore
      slug
      name
      projectType
      pifEnabled
      slug
      logoCloudinaryPath
      title {
        ... on Node {
          id
        }
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                name
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      name
                      description
                      episodeNumber
                      posterCloudinaryPath
                      posterLandscapeCloudinaryPath
                      slug
                      subtitle
                      duration
                      project {
                        id
                        slug
                      }
                      watchPosition {
                        id
                        position
                      }
                      ...ContentAvailability
                      cast {
                        name
                        character
                        images {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                      crew {
                        name
                        job
                        images {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                      ... on ContentDisplayable {
                        id
                        still: image(aspect: "16:9", category: STILL_1) {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentSharable {
          isGuildShareAvailable
        }
        ... on ContentPeople {
          cast {
            name
            character
            images {
              aspect
              category
              cloudinaryPath
            }
          }
          crew {
            name
            job
            images {
              aspect
              category
              cloudinaryPath
            }
          }
        }
        ... on ContentWatchableAvailability {
          watchableAvailabilityStatus
          watchableReasons {
            __typename
            ... on LinkshareAvailabilityAction {
              startDatetime
            }
          }
        }
        ... on ContentDisplayable {
          id
          contentRatings {
            country
            rating
            summary
            source
          }
          landscapeNonTitleImage: image(aspect: "16:9", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          portraitNonTitleImage: image(aspect: "2:3", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          squareNonTitleImage: image(aspect: "1:1", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
            aspect
            category
            cloudinaryPath
          }
          portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
            aspect
            category
            cloudinaryPath
          }
          logo: image(aspect: "9:5", category: LOGO) {
            aspect
            category
            cloudinaryPath
          }
          mediumDescription: description(version: MEDIUM)
          shortDescription: description(version: SHORT)
        }
      }
      franchiseDetails {
        watchSequence
        franchiseId
        slug
      }
    }
  }
`

export const GET_WATCH_POSITION_FOR_GUID = gql`
  query getWatchPositionForGuid($globalId: String!) {
    episodeByGlobalId(globalId: $globalId) {
      id
      source {
        duration
      }
      watchPosition {
        id
        position
        watchableGuid
      }
    }
  }
`

export const GET_EPISODE_BY_GUID_QUERY = gql`
  ${EpisodeGuildEarlyAccessFragment}
  query getEpisodeByGuid($guid: ID!, $streamingArgs: StreamingUrlArgs) {
    episode(guid: $guid) {
      id
      guid
      slug
      episodeNumber
      seasonNumber
      seasonId
      subtitle
      description
      name
      posterCloudinaryPath
      posterLandscapeCloudinaryPath
      projectSlug
      releaseDate
      source {
        credits
        duration
        url(input: $streamingArgs)
      }
      ...EpisodeGuildEarlyAccess
    }
  }
`

/**
 * This is intended for client side project, season, episode, etc. data where there is user data also being fetched with
 * authentication. We have been retrieving this on the backend through getServerSideProps for WatchEpisodeView
 * We should move to getStaticProps there and use the `getProjectAndEpisodeQuery` query without user data long term.
 */
export const GET_EPISODE_AND_USER_WATCH_DATA_QUERY = gql`
  ${EpisodeGuildEarlyAccessFragment}
  query getEpisodeAndUserWatchData(
    $guid: ID!
    $projectSlug: String!
    $authenticated: Boolean = false
    $reactionsRollupInterval: Int = 4000
    $streamingArgs: StreamingUrlArgs
  ) {
    project(slug: $projectSlug) {
      id
      projectType
      pifEnabled
      discoveryPosterCloudinaryPath
      discoveryPosterLandscapeCloudinaryPath
      discoveryPosterTransformation
      discoveryVideoLandscapeUrl
      name
      slug
      logoCloudinaryPath
      publisher {
        name
      }
      trailers {
        id
        name
        source {
          duration
          url(input: $streamingArgs)
        }
      }
      title {
        ... on ContentSharable {
          isGuildShareAvailable
        }
        ... on ContentWatchableAvailability {
          watchableAvailabilityStatus
          watchableAt
          actionsToWatch {
            __typename
            ... on GuildAvailabilityAction {
              startDatetime
            }
          }
        }
        ... on ContentDisplayable {
          id
          landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
            aspect
            category
            cloudinaryPath
          }
          landscapeTitleImage: image(aspect: "16:9", category: TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          landscapeAngelImage: image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
            aspect
            category
            cloudinaryPath
          }
          landscapeAngelImage2: image(aspect: "16:9", category: ANGEL_KEY_ART_2) {
            aspect
            category
            cloudinaryPath
          }
          landscapeAngelImage3: image(aspect: "16:9", category: ANGEL_KEY_ART_3) {
            aspect
            category
            cloudinaryPath
          }
          portraitTitleImage: image(aspect: "2:3", category: TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          portraitAngelImage: image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
            aspect
            category
            cloudinaryPath
          }
          portraitAngelImage2: image(aspect: "2:3", category: ANGEL_KEY_ART_2) {
            aspect
            category
            cloudinaryPath
          }
          portraitAngelImage3: image(aspect: "2:3", category: ANGEL_KEY_ART_3) {
            aspect
            category
            cloudinaryPath
          }
          squareNonTitleImage: image(aspect: "1:1", category: NON_TITLE_ART) {
            aspect
            category
            cloudinaryPath
          }
          logoImage: image(aspect: "original", category: LOGO) {
            aspect
            category
            cloudinaryPath
          }
        }
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                name
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      name
                      episodeNumber
                      subtitle
                      description
                      slug
                      posterCloudinaryPath
                      posterLandscapeCloudinaryPath
                      releaseDate
                      introEndTime
                      introStartTime
                      url(input: $streamingArgs)
                      duration
                      credits
                      vmapUrl
                      watchPosition {
                        id
                        position
                      }
                      watchableAvailabilityStatus
                      watchableAt
                      actionsToWatch {
                        __typename
                        ... on GuildAvailabilityAction {
                          startDatetime
                        }
                      }
                      watchableReasons {
                        __typename
                        ... on GuildAvailabilityAction {
                          startDatetime
                        }
                      }
                      listableReasons {
                        __typename
                      }
                      season {
                        id
                        seasonNumber
                      }
                      project {
                        slug
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    episode(guid: $guid) {
      description
      episodeNumber
      guid
      id
      name
      posterCloudinaryPath
      posterLandscapeCloudinaryPath
      projectSlug
      releaseDate
      seasonId
      seasonNumber
      slug
      subtitle
      source {
        credits
        duration
        url(input: $streamingArgs)
      }
      unavailableReason
      upNext {
        id
        projectSlug
        guid
        seasonNumber
        episodeNumber
        subtitle
      }
      vmapUrl
      watchPosition {
        id
        position
      }
      ...EpisodeGuildEarlyAccess
    }
    user @include(if: $authenticated) {
      id
      videoReactions(videoId: $guid, rollupInterval: $reactionsRollupInterval) {
        id
        momentId
        reactedAt
        videoGuid
        viewerId
      }
    }
  }
`

export const GET_START_WATCHING_QUERY = gql`
  query getStartWatching {
    projects {
      discoveryPosterLandscapeCloudinaryPath
      discoveryVideoUrl
      discoveryPosterTransformation
      discoveryPosterCloudinaryPath
      logoCloudinaryPath
      name
      projectType
      slug
      title {
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      episodeNumber
                      name
                      posterLandscapeCloudinaryPath
                      slug
                      subtitle
                      ... on ContentWatchableAvailability {
                        watchableAvailabilityStatus
                        watchableReasons {
                          __typename
                          ... on GuildAvailabilityAction {
                            startDatetime
                          }
                        }
                        actionsToWatch {
                          __typename
                          ... on GuildAvailabilityAction {
                            startDatetime
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentMovie {
          id
          watchableAvailabilityStatus
        }
        ... on ContentSpecial {
          id
          watchableAvailabilityStatus
        }
      }
    }
    viewer {
      region
    }
  }
`

/**
 * This is intended for server side project, season, episode, etc. data where there is NO user data also being fetched with
 * authentication. We have been retrieving this on the backend through getServerSideProps mainly for WatchEpisodeView.
 * We should move to getStaticProps there and use this query without user data long term and on the client use the
 * `GET_EPISODE_AND_USER_WATCH_DATA_QUERY` query
 */
export const getProjectAndEpisodeQuery = gql`
  ${EpisodeGuildEarlyAccessFragment}
  query getProjectAndEpisode($guid: ID!, $projectSlug: String!, $streamingArgs: StreamingUrlArgs) {
    project(slug: $projectSlug) {
      id
      public
      projectType
      pifEnabled
      name
      slug
      title {
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                name
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      name
                      episodeNumber
                      slug
                      subtitle
                      description
                      posterCloudinaryPath
                      posterLandscapeCloudinaryPath
                      duration
                      credits
                      project {
                        id
                        slug
                      }
                      season {
                        id
                        seasonNumber
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentMovie {
          id
          watchableAvailabilityStatus
        }
        ... on ContentSpecial {
          id
          watchableAvailabilityStatus
        }
      }
      logoCloudinaryPath
      publisher {
        name
      }
      videos(page: "trailer") {
        id
        posterLandscapeCloudinaryPath
        source {
          duration
          url(input: $streamingArgs)
        }
        title
      }
    }
    episode(guid: $guid) {
      id
      guid
      slug
      episodeNumber
      seasonNumber
      seasonId
      subtitle
      description
      name
      posterCloudinaryPath
      posterLandscapeCloudinaryPath
      projectSlug
      releaseDate
      vmapUrl
      source {
        credits
        duration
        url(input: $streamingArgs)
      }
      upNext {
        id
        projectSlug
        guid
        seasonNumber
        episodeNumber
        subtitle
      }
      introEndTime
      introStartTime
      ...EpisodeGuildEarlyAccess
    }
  }
`
export const GET_MOMENT_ID_FROM_STORY = gql`
  query getMomentIdFromStory($userStoryId: ID!) {
    userStory(id: $userStoryId) {
      momentId
    }
  }
`

export const GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT = gql`
  query getAvailableCollectibleOffersByProject($input: AvailableCollectibleOffersInput!) {
    availableCollectibleOffers(input: $input) {
      id
      description
      name
      image
      projectId
      products {
        ... on CollectibleProductMoment {
          __typename
          id
          name
          description
          momentId
          price
          quantity
          image
          moment {
            id
            title
            type
            availableFrames
            reactionCount
            thumbnail
            startTime
            description
            episode {
              show {
                name
              }
              season {
                seasonNumber
              }
              episodeNumber
              description
            }
            defaultOffer {
              id
            }
          }
        }
        ... on CollectibleProductSignature {
          __typename
          id
          signerName
          signerUuid
          signerImage
          signerAssociation
          signerRole
          price
          quantity
          signerId
        }
        ... on CollectibleProductKeepsake {
          __typename
          shippingTier
          shippingDays
          shippingCost
          quantity
          price
          name
          id
          dimension
          description
          image
        }
        ... on CollectibleProductGuild {
          __typename
          id
        }
        ... on CollectibleProductPack {
          description
          id
          image
          images
          name
          price
          quantity
          soldOut
        }
      }
    }
  }
`

export const GET_BASIC_COLLECTIBLES_OFFERS_BY_PROJECT = gql`
  query availableCollectibleOffers($input: AvailableCollectibleOffersInput!) {
    availableCollectibleOffers(input: $input) {
      name
      image
      id
      description
      products {
        ... on CollectibleProductPack {
          id
          images
          image
        }
        ... on CollectibleProductKeepsake {
          id
        }
        ... on CollectibleProductSignature {
          id
          description
        }
        ... on CollectibleProductMoment {
          id
          description
          momentId
        }
      }
    }
  }
`

export const GET_AVAILABLE_MOMENT = gql`
  query getAvailableMoment($momentId: ID!) {
    moment(id: $momentId) {
      id
      title
      type
      reactionCount
      thumbnail
      episode {
        season {
          seasonNumber
        }
        show {
          name
        }
        episodeNumber
      }
      defaultOffer {
        description
        id
        name
        products {
          ... on CollectibleProductMoment {
            id
            name
            description
            momentId
            price
            quantity
          }
          ... on CollectibleProductSignature {
            id
            signerName
            signerUuid
            signerImage
            price
            quantity
            signerId
          }
          ... on CollectibleProductKeepsake {
            shippingTier
            shippingDays
            shippingCost
            quantity
            price
            name
            id
            dimension
            description
          }
          ... on CollectibleProductGuild {
            id
          }
        }
      }
    }
  }
`

export const GET_EPISODE_FROM_MOMENT = gql`
  query getEpisodeFromMoment($momentId: ID!, $streamingArgs: StreamingUrlArgs) {
    moment(id: $momentId) {
      id
      title
      startTime
      thumbnail
      description
      type
      episode {
        id
        guid
        slug
        episodeNumber
        seasonNumber
        seasonId
        subtitle
        description
        name
        posterCloudinaryPath
        projectSlug
        releaseDate
        vmapUrl
        source {
          credits
          duration
          url(input: $streamingArgs)
        }
      }
    }
  }
`

export const GET_FRAME_BY_ID = gql`
  query getFrameById($frameId: ID!) {
    frame(id: $frameId) {
      id
      uri
      frameNumber
      smpteTimecode
      episode {
        canonId
      }
      nifty {
        id
        ownedSince
        signatures {
          signatureUrl
          cloudinaryPath
        }
        gift {
          id
          createdAt
          message
          toFirstName
          toLastName
          gifter {
            uuid
            firstName
            lastName
          }
        }
      }
      user {
        uuid
        firstName
        lastName
        image
      }
    }
  }
`

export const GET_EPISODE_FROM_MOMENT_WITH_USER = gql`
  query getEpisodeFromMomentWithUser($momentId: ID!, $shareUserId: String!, $streamingArgs: StreamingUrlArgs) {
    moment(id: $momentId) {
      id
      title
      type
      startTime
      thumbnail
      description
      owner(id: $shareUserId) {
        id
        profile {
          id
          image(fallbackToInitials: true)
          firstName
          lastName
        }
        story: userStoryByMoment(momentId: $momentId) {
          id
          text
          videoUri
          createdAt
          posterCloudinaryPath
        }
      }
      episode {
        id
        guid
        slug
        episodeNumber
        seasonNumber
        seasonId
        subtitle
        description
        name
        posterCloudinaryPath
        projectSlug
        releaseDate
        vmapUrl
        source {
          credits
          duration
          url(input: $streamingArgs)
        }
      }
    }
    userStories(userStoriesInput: { where: { momentId: $momentId, userId: $shareUserId }, take: 1 }) {
      stories {
        id
        createdAt
        posterCloudinaryPath
        text
        videoUri
        user {
          id
          profile {
            id
            firstName
            lastName
            image(fallbackToInitials: true)
          }
        }
      }
    }
  }
`

export const GET_LIGHT_PROJECT_FOR_SHARE = gql`
  query getLightProjectForShare($projectSlug: String!) {
    project(slug: $projectSlug) {
      id
      name
      slug
      projectType
      pifEnabled
    }
  }
`

export const getProjectAndEpisodeQueryForDeepLink = gql`
  query getProjectAndEpisodeQueryForDeepLink($guid: ID!, $projectSlug: String!) {
    project(slug: $projectSlug) {
      id
      projectType
      discoveryPosterLandscapeCloudinaryPath
      name
      slug
      title {
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      project {
                        id
                        slug
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentMovie {
          id
        }
        ... on ContentSpecial {
          id
        }
      }
    }
    episode(guid: $guid) {
      description
      episodeNumber
      seasonNumber
      subtitle
      projectSlug
    }
  }
`

export const getVideoSeoQuery = gql`
  query getVideoSeo($projectSlug: String!, $streamingArgs: StreamingUrlArgs) {
    project(slug: $projectSlug) {
      id
      logoCloudinaryPath
      name
      projectType
      public
      trailers {
        id
        name
        source {
          url(input: $streamingArgs)
        }
      }
      videos {
        id
        guid
        slug
        title
        subtitle
        posterLandscapeCloudinaryPath
        page
        source {
          credits
          duration
          url(input: $streamingArgs)
        }
      }
    }
  }
`

export const GET_CONTENT_VIDEO = gql`
  query getContentVideo($guid: ID!, $streamingArgs: StreamingUrlArgs) {
    getContent(id: $guid) {
      ... on Node {
        id
      }
      ... on ContentWatchable {
        id
        url(input: $streamingArgs)
        credits
        duration
        watchPosition {
          id
          position
        }
      }
      ... on ContentDisplayable {
        id
        name
        description
        logo: image(category: LOGO, aspect: "original") {
          cloudinaryPath
        }
        nonTitleArt: image(category: NON_TITLE_ART, aspect: "16:9") {
          cloudinaryPath
        }
        angelKeyArt: image(category: ANGEL_KEY_ART_1, aspect: "16:9") {
          cloudinaryPath
        }
      }
      ... on ContentSharable {
        isGuildShareAvailable
      }
      ... on ContentEpisode {
        id
        __typename
        name
        description
        duration
        posterLandscapeCloudinaryPath
        vmapUrl
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
        }
      }
      ... on ContentTrailer {
        id
        __typename
        name
        description
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
        }
      }
      ... on ContentBonus {
        id
        __typename
        name
        title
        subtitle
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
        }
      }
      ... on ContentSneakPeek {
        id
        __typename
        name
        title
        subtitle
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
        }
      }
      ... on ContentWatchableAvailability {
        watchableAvailabilityStatus
        watchableAt
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
          ... on PubliclyAvailable {
            startDatetime
          }
        }
      }
    }
  }
`

export const GET_CONTENT_EPISODE = gql`
  query getContentEpisode($guid: ID!, $streamingArgs: StreamingUrlArgs) {
    getContent(id: $guid) {
      ... on Node {
        id
      }
      ... on ContentWatchable {
        id
        url(input: $streamingArgs)
        credits
        duration
        vmapUrl
        watchPosition {
          id
          position
          updatedAt
        }
        credits
      }
      ... on ContentDisplayable {
        id
        name
        description
        genres
        contentRatings {
          country
          rating
          summary
          source
        }
        logo: image(category: LOGO, aspect: "original") {
          cloudinaryPath
        }
        nonTitleArt: image(category: NON_TITLE_ART, aspect: "16:9") {
          cloudinaryPath
        }
      }
      ... on ContentWatchableAvailability {
        watchableAvailabilityStatus
        watchableAt
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
          ... on PubliclyAvailable {
            startDatetime
          }
        }
      }
      ... on ContentSharable {
        isGuildShareAvailable
      }
      ... on ContentEpisode {
        id
        __typename
        name
        slug
        description
        introStartTime
        introEndTime
        originalReleaseDate
        posterLandscapeCloudinaryPath
        episodeNumber
        season {
          id
          seasonNumber
          name
        }
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
          pifEnabled
        }
      }
      ... on ContentMovie {
        id
        __typename
        name
        slug
        description
        introStartTime
        introEndTime
        originalReleaseDate
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
          pifEnabled
        }
      }
      ... on ContentSpecial {
        id
        __typename
        name
        slug
        description
        introStartTime
        introEndTime
        originalReleaseDate
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
        }
      }
      ... on ContentSneakPeek {
        id
        __typename
        name
        slug
        title
        introStartTime
        introEndTime
        originalReleaseDate
        posterLandscapeCloudinaryPath
        project {
          id
          slug
          name
          public
          logoCloudinaryPath
          discoveryPosterLandscapeCloudinaryPath
          pifEnabled
        }
      }
    }
  }
`

export const GET_EMBED_CONTENT = gql`
  query getEmbedContent($guid: ID!, $streamingArgs: StreamingUrlArgs) {
    getContent(id: $guid) {
      ... on Node {
        id
      }
      ... on ContentWatchable {
        id
        url(input: $streamingArgs)
        credits
        duration
        vmapUrl
        watchPosition {
          id
          position
        }
      }
      ... on ContentDisplayable {
        id
        name
        description
      }
      ... on ContentEpisode {
        id
        __typename
        name
        subtitle
        slug
        description
        episodeNumber
        originalReleaseDate
        season {
          id
          seasonNumber
        }
        project {
          id
          slug
          name
        }
      }
      ... on ContentMovie {
        id
        __typename
        name
        subtitle
        slug
        description
        originalReleaseDate
        project {
          id
          slug
          name
        }
      }
      ... on ContentSpecial {
        id
        __typename
        name
        subtitle
        slug
        description
        originalReleaseDate
        project {
          id
          slug
          name
        }
      }
      ... on ContentTrailer {
        id
        __typename
        name
        description
        subtitle
        project {
          id
          slug
          name
        }
      }
      ... on ContentBonus {
        id
        __typename
        name
        title
        subtitle
        project {
          id
          slug
          name
        }
      }
      ... on ContentSneakPeek {
        id
        __typename
        name
        slug
        title
        subtitle
        originalReleaseDate
        project {
          id
          slug
          name
        }
      }
    }
  }
`

export const getProjectAndFaqQuery = contentfulGQL/* GraphQL */ `
  query getProjectAndFaq($projectSlug: String!, $locale: String, $preview: Boolean) {
    projectCollection(limit: 1, where: { slug: $projectSlug }, locale: $locale, preview: $preview) {
      items {
        contentCatalogId
        distributionStage
        fundingPurpose
        currentlyFunding
        crowdfundingSlug
        mission
        showOverview
        hasHubPage
        showMetaDescription
        theaterBackgroundPosition
        pifConnectedCarouselCollection {
          items {
            variantKey
            title
            subtitle
            href
            imagesCollection {
              items {
                item
              }
            }
          }
        }
        defaultPifConnectedCarousel {
          variantKey
          title
          subtitle
          href
          imagesCollection {
            items {
              item
            }
          }
        }
        pifConnectedCarouselExperimentKey
        overrideTheaterWatchButtonBuyTicketsUrl
        overrideTheaterWatchButtonTrailerUrl
        rottenTomatoes: criticsCollection(limit: 1, where: { sort: "Rotten Tomatoes" }) {
          items {
            rating
            author
            icon
          }
        }
        audience: criticsCollection(limit: 1, where: { sort: "Audience Scores" }) {
          items {
            rating
            author
            icon
          }
        }
        cinema: criticsCollection(limit: 1, where: { sort: "CinemaScore" }) {
          items {
            rating
            author
            icon
          }
        }
        IMDb: criticsCollection(limit: 1, where: { sort: "IMDb" }) {
          items {
            rating
            author
            icon
          }
        }
      }
    }
    faqCollection(
      where: { location: "watch", OR: [{ project: { slug: $projectSlug } }, { project: { slug: "angel-studios" } }] }
      locale: $locale
      preview: $preview
      order: ordinal_ASC
    ) {
      items {
        answer {
          json
        }
        ordinal
        question
      }
    }
    downloadableCollection(
      where: { OR: [{ location_exists: false }, { location: "browse" }], project: { slug: $projectSlug } }
      locale: $locale
      preview: $preview
      order: ordinal_ASC
    ) {
      items {
        href
        image
        title
        type
      }
    }
  }
`

export const getContentfulProjectConfig = contentfulGQL/* GraphQL */ `
  query getContentfulProjectConfig($projectSlug: String!, $locale: String) {
    projectCollection(limit: 1, where: { slug: $projectSlug }, locale: $locale) {
      items {
        fundingPurpose
        currentlyFunding
        crowdfundingSlug
        mission
        showOverview
        hasHubPage
        theaterBackgroundPosition
        pifPerkEarlyAccessMinimumPrice
        pifFeaturedOfferId
        pifEarlyAccessPerkId
        tributeOfferIDs
        pifFeaturedOfferText {
          json
        }
        shouldShowPifExplainerSection
        pifCustomInfoSection {
          json
        }
        pifFundingInfoModal {
          json
        }
        pifConnectedCarouselCollection {
          items {
            variantKey
            title
            subtitle
            href
            imagesCollection {
              items {
                item
              }
            }
          }
        }
        defaultPifConnectedCarousel {
          variantKey
          title
          subtitle
          href
          imagesCollection {
            items {
              item
            }
          }
        }
        pifConnectedCarouselExperimentKey
        shopifyStore {
          storeUrl
          accessToken
          handle
          storeHref
        }
      }
    }
  }
`

export const getProjectSitemapsQuery = gql`
  query getProjectSitemaps {
    projects {
      id
      name
      livestreams(includeGuildOnly: true) {
        guid
        id
        endedAt
      }
      livestreamsCount
      pifEnabled
      projectType
      public
      slug
      videos {
        id
        guid
        page
      }
    }
  }
`

export const getProjectDetailsQuery = gql`
  query getProjectDetails($slug: String!) {
    project(slug: $slug) {
      name
      pifEnabled
      projectType
      slug
    }
  }
`

export const GET_GUILD_ACCESS_PROJECTS = contentfulGQL/* GraphQL */ `
  query getGuildAccessProjects {
    projectCollection(where: { distributionStage: "early-access" }) {
      items {
        name
        slug
        showOverview
        filmDetails {
          moviePoster
        }
      }
    }
  }
`

export const getCurrentLivestreamQuery = gql`
  query getCurrentLivestream($projectId: ID!) {
    currentLivestream(projectId: $projectId) {
      guid
      name
      bannerCloudinaryPath
      live
      id
      startAt
      isEnded
      unavailableReason
      description
    }
  }
`

export const GET_PROJECT_GUILD_SCORE = gql`
  query fetchProjectGuildScore($projectSlug: String!) {
    project(slug: $projectSlug) {
      id
      name
      highestScore
    }
  }
`

export const GET_PROJECT_SEO_METADATA = gql`
  query fetchProjectSeoMetadata($projectSlug: String!) {
    project(slug: $projectSlug) {
      id
      title {
        ... on ContentDisplayable {
          id
          name
          mediumDescription: description(version: MEDIUM)
          titleArt: image(aspect: "16:9", category: TITLE_ART) {
            cloudinaryPath
          }
        }
      }
    }
  }
`

export const GET_PROJECT_LOGO = gql`
  query getProjectLogo($slug: String!) {
    project(slug: $slug) {
      title {
        ... on ContentDisplayable {
          logo: image(aspect: "original", category: LOGO) {
            cloudinaryPath
          }
        }
      }
    }
  }
`

export const GET_WATCHABLE_EPISODE = gql`
  query getWatchableEpisode($guid: ID!) {
    getContent(id: $guid) {
      ... on ContentEpisode {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        watchPosition {
          id
          position
        }
      }
    }
  }
`

export const GET_PREVIEWABLE_CONTENT = gql`
  query getPreviewableContent($guid: ID!) {
    getContent(id: $guid) {
      ... on ContentDisplayable {
        id
        name
        longDescription: description(version: LONG)
        mediumDescription: description(version: MEDIUM)
        shortDescription: description(version: SHORT)
        logo: image(aspect: "9:5", category: LOGO) {
          cloudinaryPath
        }
        keyArt1: image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
          cloudinaryPath
        }
        keyArt2: image(aspect: "16:9", category: ANGEL_KEY_ART_2) {
          cloudinaryPath
        }
        keyArt3: image(aspect: "16:9", category: ANGEL_KEY_ART_3) {
          cloudinaryPath
        }
        titleArt: image(aspect: "16:9", category: TITLE_ART) {
          cloudinaryPath
        }
      }
      ... on ContentWatchable {
        url
      }
      ... on ContentEpisode {
        id
        __typename
        project {
          id
          name
          logoCloudinaryPath
          title {
            ... on ContentSeries {
              id
              seasons(first: 1) {
                edges {
                  node {
                    id
                    episodes(first: 100) {
                      edges {
                        node {
                          __typename
                          id
                          name
                          episodeNumber
                          ... on ContentWatchableAvailability {
                            watchableAvailabilityStatus
                            watchableReasons {
                              __typename
                              ... on LinkshareAvailabilityAction {
                                startDatetime
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        preview {
          id
          m3u8Uri
          duration
        }
      }
      ... on ContentSpecial {
        id
        __typename
        project {
          id
          name
          logoCloudinaryPath
        }
        preview {
          id
          m3u8Uri
          duration
        }
      }
    }
  }
`

export const GET_WATCHABLE_AVAILABILITY = gql`
  query getWatchableAvailability($guid: ID!) {
    getContent(id: $guid) {
      ... on ContentEpisode {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentMovie {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentSpecial {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentTrailer {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentBonus {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentSneakPeek {
        id
        watchableAvailabilityStatus
        watchableReasons {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
      }
      ... on ContentSeries {
        id
        firstWatchable {
          __typename
          ... on ContentEpisode {
            id
            watchableAvailabilityStatus
            watchableReasons {
              __typename
              ... on GuildAvailabilityAction {
                startDatetime
              }
            }
            actionsToWatch {
              __typename
              ... on GuildAvailabilityAction {
                startDatetime
              }
            }
          }
        }
      }
      ... on ContentWatchable {
        id
        vmapUrl
      }
    }
  }
`

export const GET_PROJECT_HOVER_DATA = gql`
  query getWatchableHoverContent($guid: ID!) {
    getContent(id: $guid) {
      ... on ContentDisplayable {
        id
        name
        genres
        contentRatings {
          country
          rating
          summary
          source
        }
        longDescription: description(version: LONG)
        mediumDescription: description(version: MEDIUM)
        shortDescription: description(version: SHORT)
        angelKeyArt1: image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
          cloudinaryPath
        }
        angelKeyArt2: image(aspect: "2:3", category: ANGEL_KEY_ART_2) {
          cloudinaryPath
        }
        angelKeyArt3: image(aspect: "2:3", category: ANGEL_KEY_ART_3) {
          cloudinaryPath
        }
        still1: image(aspect: "16:9", category: STILL_1) {
          cloudinaryPath
        }
        still2: image(aspect: "16:9", category: STILL_2) {
          cloudinaryPath
        }
        still3: image(aspect: "16:9", category: STILL_3) {
          cloudinaryPath
        }
        nonTitleArt: image(aspect: "16:9", category: NON_TITLE_ART) {
          cloudinaryPath
        }
        backdrop: image(aspect: "16:9", category: BACKDROP) {
          cloudinaryPath
        }
        logo: image(aspect: "9:5", category: LOGO) {
          cloudinaryPath
        }
      }
      ... on ContentEpisode {
        id
        __typename
        originalReleaseDate
        preview {
          id
          m3u8Uri
          duration
        }
        project {
          id
          slug
          highestScore
          logoCloudinaryPath
        }
      }
      ... on ContentMovie {
        id
        __typename
        originalReleaseDate
        preview {
          id
          m3u8Uri
          duration
        }
        project {
          id
          slug
          highestScore
        }
      }
      ... on ContentSpecial {
        id
        __typename
        originalReleaseDate
        preview {
          id
          m3u8Uri
          duration
        }
        project {
          id
          slug
          highestScore
        }
      }
      ... on ContentSeries {
        id
        __typename
        originalReleaseDate
        preview {
          id
          m3u8Uri
          duration
        }
        firstWatchable {
          __typename
          ... on ContentEpisode {
            id
            watchableAvailabilityStatus
            episodeNumber
            season {
              id
              seasonNumber
            }
          }
        }
        project {
          id
          slug
          highestScore
        }
      }
      ... on ContentSharable {
        isGuildShareAvailable
      }
      ... on ContentWatchable {
        duration
        watchPosition {
          id
          position
        }
      }
      ... on ContentWatchableAvailability {
        watchableAvailabilityStatus
        actionsToWatch {
          __typename
        }
        watchableReasons {
          __typename
          ... on LinkshareAvailabilityAction {
            startDatetime
          }
        }
      }
    }
  }
`

export const GET_PROJECT_HOVER_DATA_BY_SLUG = gql`
  query getWatchableHoverContentBySlug($slug: String!) {
    project(slug: $slug) {
      id
      slug
      highestScore
      title {
        ... on ContentDisplayable {
          id
          name
          genres
          contentRatings {
            country
            rating
            summary
            source
          }
          longDescription: description(version: LONG)
          mediumDescription: description(version: MEDIUM)
          shortDescription: description(version: SHORT)
          angelKeyArt1: image(aspect: "2:3", category: ANGEL_KEY_ART_1) {
            cloudinaryPath
          }
          angelKeyArt2: image(aspect: "2:3", category: ANGEL_KEY_ART_2) {
            cloudinaryPath
          }
          angelKeyArt3: image(aspect: "2:3", category: ANGEL_KEY_ART_3) {
            cloudinaryPath
          }
          still1: image(aspect: "16:9", category: STILL_1) {
            cloudinaryPath
          }
          still2: image(aspect: "16:9", category: STILL_2) {
            cloudinaryPath
          }
          still3: image(aspect: "16:9", category: STILL_3) {
            cloudinaryPath
          }
          nonTitleArt: image(aspect: "16:9", category: NON_TITLE_ART) {
            cloudinaryPath
          }
          backdrop: image(aspect: "16:9", category: BACKDROP) {
            cloudinaryPath
          }
          logo: image(aspect: "9:5", category: LOGO) {
            cloudinaryPath
          }
        }
        ... on ContentPreviewable {
          preview {
            id
            m3u8Uri
            duration
          }
        }
        ... on ContentSharable {
          isGuildShareAvailable
        }
        ... on ContentWatchable {
          duration
          watchPosition {
            id
            position
          }
        }
        ... on ContentWatchableAvailability {
          watchableAvailabilityStatus
          actionsToWatch {
            __typename
          }
          watchableReasons {
            __typename
            ... on LinkshareAvailabilityAction {
              startDatetime
            }
          }
        }
      }
    }
  }
`

export const GET_DISCOVER_SNEAK_PEEKS = gql`
  query getDiscoverSneakPeeks {
    listWatchables(first: 75, input: { filter: { type: { eq: SNEAK_PEEK } } }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          ... on ContentSneakPeek {
            id
            slug
            subtitle
            name
            duration
            posterLandscapeCloudinaryPath
            project {
              id
              releaseDate
              name
              slug
              title {
                ... on ContentDisplayable {
                  id
                  image(aspect: "16:9", category: NON_TITLE_ART) {
                    cloudinaryPath
                  }
                }
              }
            }
            watchPosition {
              id
              position
            }
          }
        }
      }
    }
  }
`

export const FETCH_SERIES_EPISODE_STILLS = gql`
  query getSeriesEpisodeStillsBySlug($slug: String!) {
    project(slug: $slug) {
      title {
        __typename
        ... on Node {
          id
        }
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                episodes(first: 100) {
                  edges {
                    node {
                      __typename
                      id
                      posterLandscapeCloudinaryPath
                      ... on ContentDisplayable {
                        id
                        still1: image(aspect: "4:3", category: STILL_1) {
                          aspect
                          category
                          cloudinaryPath
                        }
                        still2: image(aspect: "4:3", category: STILL_2) {
                          aspect
                          category
                          cloudinaryPath
                        }
                        still3: image(aspect: "4:3", category: STILL_3) {
                          aspect
                          category
                          cloudinaryPath
                        }
                        still4: image(aspect: "4:3", category: STILL_4) {
                          aspect
                          category
                          cloudinaryPath
                        }
                        still5: image(aspect: "4:3", category: STILL_5) {
                          aspect
                          category
                          cloudinaryPath
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GET_PROJECT_VIDEOS_QUERY = gql`
  query getVideosByPageForProjectWithId($projectSlug: String!, $page: String) {
    project(slug: $projectSlug) {
      id
      videos(page: $page) {
        guid
        title
        subtitle
        posterLandscapeCloudinaryPath
        watchPosition {
          position
        }
        source {
          duration
        }
        page
      }
    }
  }
`

export const GET_NEXT_CONTENT_FRAG = gql`
  fragment NextContent on Content {
    __typename
    ... on Node {
      id
    }
    ... on ContentEpisode {
      id
      name
      posterLandscapeCloudinaryPath
      episodeNumber
      subtitle
      watchableAt
      watchableAvailabilityStatus
      actionsToWatch {
        __typename
        ... on GuildAvailabilityAction {
          startDatetime
        }
      }
      image(aspect: "16:9", category: STILL_1) {
        cloudinaryPath
      }
      project {
        id
        name
        slug
      }
      season {
        id
        seasonNumber
      }
    }
    ... on ContentSpecial {
      id
      subtitle
      posterLandscapeCloudinaryPath
      watchableAt
      watchableAvailabilityStatus
      actionsToWatch {
        __typename
        ... on GuildAvailabilityAction {
          startDatetime
        }
      }
      image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
        cloudinaryPath
      }
      project {
        id
        name
      }
    }
    ... on ContentMovie {
      id
      name
      posterLandscapeCloudinaryPath
      watchableAt
      watchableAvailabilityStatus
      actionsToWatch {
        __typename
        ... on GuildAvailabilityAction {
          startDatetime
        }
      }
      image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
        cloudinaryPath
      }
    }
  }
`

const PROJECT_WITH_MOVIE_FRAGMENT = gql`
  fragment ProjectWithMovie on Project {
    id
    title {
      __typename
      ... on Node {
        id
      }
      ... on ContentMovie {
        id
        name
        subtitle
        posterLandscapeCloudinaryPath
        watchableAt
        watchableAvailabilityStatus
        actionsToWatch {
          __typename
          ... on GuildAvailabilityAction {
            startDatetime
          }
        }
        image(aspect: "16:9", category: ANGEL_KEY_ART_1) {
          cloudinaryPath
        }
      }
    }
  }
`

/**
  The query only returns logical up next.
  Does not return things that might be gated for guild or other things.
  Does not return up next from different projects.

  For trailers and sneak peeks, it returns the next movie in the project if it is available.
*/
export const GET_NEXT_CONTENT = gql`
  ${GET_NEXT_CONTENT_FRAG}
  ${PROJECT_WITH_MOVIE_FRAGMENT}
  query getNextContent($contentGuid: ID!) {
    nextContent: getNextContent(id: $contentGuid) {
      ...NextContent
    }
    potentialTrailerOrSneakPeekMovie: getContent(id: $contentGuid) {
      __typename
      ... on ContentTrailer {
        id
        project {
          ...ProjectWithMovie
        }
      }
      ... on ContentSneakPeek {
        id
        project {
          ...ProjectWithMovie
        }
      }
      ... on Node {
        id
      }
    }
  }
`

export const GET_THEATRICAL_RELEASE_BY_SLUG = gql`
  query getTheatricalReleaseBySlug($slug: String!, $region: String!) {
    theatricalRelease(input: { theatricalSlug: $slug }) {
      theatricalRelease {
        id
        theatricalSlug
        critics {
          company
          scores {
            name
            value
            floatValue
          }
          certifiedFresh
          verifiedHot
        }
        region(region: $region) {
          id
          releaseDate
          ticketsStatus
        }
      }
    }
  }
`

export const GET_SITEMAP_SHOWS = gql`
  query getSitemapShows($first: Int!, $after: String) {
    listWatchables(
      first: $first
      after: $after
      input: { filter: { type: { in: [SERIES] } }, includePrerelease: false, includePresale: false }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          ... on ContentSeries {
            id
            project {
              id
              slug
              name
              releaseDate
            }
          }
        }
      }
    }
  }
`

export const GET_SITEMAP_MOVIES = gql`
  query getSitemapMovies($first: Int!, $after: String) {
    listWatchables(
      first: $first
      after: $after
      input: { filter: { type: { in: [MOVIE] } }, includePrerelease: false, includePresale: false }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          ... on ContentMovie {
            id
            project {
              id
              slug
              name
              releaseDate
            }
          }
        }
      }
    }
  }
`

/**
 * Lightweight query to look up content by ID for share pages.
 * Returns the project slug (needed for getProjectAbout) and episode metadata
 * (needed for building display fields subtitle).
 */
export const GET_SHARE_PAGE_CONTENT = gql`
  query getSharePageContent($contentId: ID!) {
    getContent(id: $contentId) {
      ... on Node {
        id
      }
      ... on ContentDisplayable {
        name
      }
      ... on ContentEpisode {
        id
        __typename
        episodeNumber
        season {
          id
          seasonNumber
        }
        project {
          id
          slug
          name
        }
      }
      ... on ContentMovie {
        id
        __typename
        project {
          id
          slug
          name
        }
      }
      ... on ContentSpecial {
        id
        __typename
        project {
          id
          slug
          name
        }
      }
      ... on ContentSeries {
        id
        __typename
        project {
          id
          slug
          name
        }
      }
    }
  }
`
